import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Image from 'next/image';
import { useClickAway } from 'ahooks';
import Button from '$/components/base/Button';
import IconFont from '$/components/IconFont';
import Tooltip from '$/components/Tooltip';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import useThemeStore from '$/store/useThemeStore';

import { mtstatTrack } from '$/utils/mtstat';
import { getEnv, navigate } from '@/common/agents';
import { getCookieHomeUrl } from '@/common/homeVersionCookie';
import { useCommonStore, setNavbarExpand } from '@/store/useCommonStore';
import useTeamStore from '@/store/useTeamStore';
import { openChooseIPModal } from '$/components/ChooseIpModal/chooseIPStore';
import type { PageInfo } from '@/common/pageType';
import { AI_TOOLS } from '@/domains/tools/constant';
import { getLocalizedAiToolRoute } from '@/domains/tools/getLocalizedAiToolRoute';
import COLLAPSE_LOGO from '../../assets/collapse-logo.png';
import COLLAPSE_LOGO_BLACK from '../../assets/collapse-logo-black.png';
import ROBONEO_TITLE from '../../assets/roboneo-title.png';
import styles from './index.module.scss';
import NavbarAssetStorageCard from './NavbarAssetStorageCard';

export { createNewCanvas, createNewFlow } from '@/utils/createRoom';

export enum HomeBar {
  Home = 'home',
  Community = 'community',
  Project = 'project',
  DataDashboard = 'data_dashboard',
  TeamProject = 'team_project',
  Assets = 'assets',
}

const NAVBAR_TRACK_LOCATION_MAP: Record<PageInfo['locationName'], string> = {
  home: 'home_page',
  project: 'project_page',
  team_project: 'team_project_page',
  community: 'community_page',
  communityDetail: 'community_detail_page',
  // IP 页面侧栏 location 尚未确认，沿用原有未知页面空值，不伪造埋点口径。
  myips: '',
  myIpDetail: '',
  // 复刻场景尚无正式埋点位置定义。
  viralRemake: '',
  // 资产独立页尚无正式埋点位置定义。
  assets: '',
  case_detail: 'case_detail_page',
  studio: 'room_detail',
  ai_flow: 'workflow_page',
  social_case: 'social_case_page',
  data_dashboard: 'data_dashboard_page',
};

// 侧边栏隐藏视频剪辑入口，并沿用 AI_TOOLS 的定义顺序。
const SIDEBAR_AI_TOOLS = AI_TOOLS.filter((tool) => tool.id !== 'ai_video');

/** 获取侧边栏展开收起埋点的页面位置。 */
function getNavbarTrackLocation(activeNav: PageInfo['locationName']) {
  return NAVBAR_TRACK_LOCATION_MAP[activeNav] || '';
}

/** 从当前 URL 读取房间 id，非房间页面兜底为空字符串。 */
function getCurrentRoomId() {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('room_id') || '';
}

interface NavbarProps {
  activeNav: PageInfo['locationName'];
  setActiveNav: (nav: PageInfo['locationName']) => void;
}

/** 共享侧栏提供导航与登录后的 IP 选择入口，埋点位置由独立映射维护。 */
function Navbar({ activeNav, setActiveNav }: NavbarProps) {
  const theme = useThemeStore((state) => state.theme);
  const languageStore = useLanguageStore((state) => state.languageStore);
  const expand = useCommonStore((state) => state.navbarExpand);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn);
  const toLogin = useCommonStore((state) => state.toLogin);
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const isTeam = useTeamStore((state) => Boolean(state.teamInfo?.oid));
  // 退出登录会清空团队信息，团队项目路由仍需保留对应入口与选中态。
  const showTeamProjectEntry = isTeam || activeNav === HomeBar.TeamProject;
  const [aiToolsExpanded, setAiToolsExpanded] = useState(false);
  const aiToolsCollapsedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--sidebar-width', expand ? '76px' : '260px');
  }, [expand]);

  useClickAway(() => {
    if (expand && aiToolsExpanded) {
      setAiToolsExpanded(false);
    }
  }, aiToolsCollapsedRef);

  const BarList = [
    {
      id: HomeBar.Home,
      name: languageStore.smartcatTranslationText(
        (v) => v.roboneo_web_home_page,
      ),
      icon: 'homeV2',
      activeIcon: 'homeV2Fill',
    },

    // 作品社区，agentteam需求产品让暂时隐藏 https://cf.meitu.com/confluence/pages/viewpage.action?pageId=675242827
    // {
    //   id: HomeBar.Community,
    //   name: languageStore.smartcatTranslationText(
    //     (v) => v.roboneo_web_works_community,
    //   ),
    //   icon: 'planet',
    // },

    showTeamProjectEntry
      ? {
        id: HomeBar.TeamProject,
        name: languageStore.smartcatTranslationText((v) => v.team_project),
        icon: 'userGroupV3Bold',
        activeIcon: 'userGroupV3Fill',
      }
      : {
        // 个人态继续展示原有的“我的项目”入口。
        id: HomeBar.Project,
        name: languageStore.smartcatTranslationText(
          (v) => v.roboneo_web_my_projects,
        ),
        icon: 'userCircle',
        activeIcon: 'userCircleFill',
      },
    // 资产入口置于短剧洞察之前，展开与收起侧栏共用此顺序。
    {
      id: HomeBar.Assets,
      name: languageStore.smartcatTranslationText((v) => v.robo_zichan),
      icon: 'folder',
      activeIcon: 'folder',
    },
    // 短剧洞察
    {
      id: HomeBar.DataDashboard,
      name: languageStore.smartcatTranslationText(
        (v) => v.robo_short_drama_insights,
      ),
      icon: 'chart',
      activeIcon: 'chartFill',
    },
  ];

  /** 点击侧栏 Logo 回首页时，沿用 cookie 中选择的新旧首页版本。 */
  const goHomepage = () => {
    navigate({
      mode: 'url',
      url: getCookieHomeUrl(getEnv().ENV === 'development'),
    });
  };

  /** 点击侧栏 AI 工具入口，按当前语言上报后跳转到 Essio 对应落地页。 */
  const aiToolClicked = (tool: any) => {
    mtstatTrack('roboneo_aifunc_enter', {
      func: tool.id,
      source: 'side_bar',
    });
    navigate({
      mode: 'router',
      pathname: getLocalizedAiToolRoute(tool.route, languageStore.languageCode) as any,
    });
  };

  /** 资产和项目入口先校验登录，登录成功后再跳转并更新选中态。 */
  const goTabItem = (id: HomeBar) => {
    const requiresLogin = id === HomeBar.Project || id === HomeBar.TeamProject || id === HomeBar.Assets;
    // 已选中的受限入口在退出登录后仍需调起登录，不能被重复点击判断拦截。
    if (activeNav === id && (!requiresLogin || isUserLoggedIn())) return;
    window.mtstat?.track('home_page_guide_click', {
      icon_name: id,
    });
    if (id === 'home') {
      window.scrollTo({ top: 0 });
    }

    // 资产与项目需登录：先调起登录；成功后再跳转对应页并高亮 tab（资料拉取失败不阻塞）。
    if (
      requiresLogin
      && !isUserLoggedIn()
    ) {
      toLogin()
        .then(async () => {
          if (!useOfficialStore.getState().isUserLoggedIn()) return;
          try {
            await initUserInfo();
          } catch {
            // 资料失败不阻塞进入目标页
          }
          navigate({
            mode: 'router',
            pathname: `/--roboneo/${id}`,
          });
          setActiveNav(id);
        })
        .catch(() => {});
      return;
    }

    // 重复点击不需要跳转
    if (activeNav === id) return;

    navigate({
      mode: 'router',
      pathname: `/--roboneo/${id}`,
    });
    setActiveNav(id);
  };

  /** 切换侧边栏展开状态，同时上报当前页面位置。 */
  const handleNavbarExpandChange = (nextExpand: boolean) => {
    mtstatTrack('roboneo_roomtop_unfold_click', {
      room_id: getCurrentRoomId(),
      type: nextExpand ? 'close' : 'open',
      location: getNavbarTrackLocation(activeNav),
    });
    setNavbarExpand(nextExpand);
  };

  /** 新建项目先校验登录态，已登录时打开角色 IP 选择弹窗。 */
  const handleCreateProject = () => {
    if (!isUserLoggedIn()) {
      useOfficialStore.getState().toLogin().catch(() => {});
      return;
    }

    openChooseIPModal(undefined, '', 'home_side_create_project');
  };

  return (
    <div className={styles.navbar_container}>
      {/* 收起 */}
      <div
        className={styles.collapse_navbar_box}
        style={{
          opacity: expand ? '1' : '0',
          zIndex: expand ? 2 : 1,
          pointerEvents: expand ? 'auto' : 'none',
        }}
      >
        {/* 展开按钮 */}
        <Tooltip title={languageStore.smartcatTranslationText((v) => v.roboneo_web_expand_sidebar)} placement="right">
          <Button type="transparent" className={classNames(styles.btn, styles.expand_btn, styles.collapsedLogoBtn)} onClick={() => handleNavbarExpandChange(!expand)}>
            <span className={styles.collapsedLogoMark}>
              <Image
                src={theme === 'light' ? COLLAPSE_LOGO_BLACK : COLLAPSE_LOGO}
                alt=""
                width={28}
                height={18}
              />
            </span>
          </Button>
        </Tooltip>

        {/* 新建 */}
        <Tooltip title={languageStore.smartcatTranslationText((v) => v.roboneo_web_project)} placement="right">
          <Button type="transparent" className={classNames(styles.btn, styles.create_btn)} onClick={handleCreateProject}>
            <IconFont
              name="plusCircleFill"
              color="currentColor"
              fontSize={18}
            />
          </Button>
        </Tooltip>

        {/* 主导航 */}
        {BarList.map((item) => (
          <Tooltip key={item.id} title={item.name} placement="right">
            <Button
              type="transparent"
              className={classNames(styles.btn, { [styles.active]: activeNav === item.id })}
              aria-label={item.name}
              onClick={() => goTabItem(item.id)}
            >
              <IconFont
                name={activeNav === item.id ? item.activeIcon : item.icon}
                color="currentColor"
                fontSize={18}
              />
            </Button>
          </Tooltip>
        ))}

        {/* AI创作工具：收起态按设计展示右侧浮层，展开状态仍与展开态共用。 */}
        <div className={styles.aiToolsCollapsedSection} ref={aiToolsCollapsedRef}>
          <Tooltip title={languageStore.smartcatTranslationText((v) => v.ai_create_tools)} placement="right">
            <Button
              type="transparent"
              className={classNames(styles.btn, styles.aiToolsCollapsedHeaderBtn, {
                [styles.active]:
                  aiToolsExpanded
                  || SIDEBAR_AI_TOOLS.some((tool) => activeNav === tool.id),
              })}
              aria-label={languageStore.smartcatTranslationText((v) => v.ai_create_tools)}
              aria-expanded={aiToolsExpanded}
              onClick={() => setAiToolsExpanded((v) => !v)}
            >
              <IconFont
                name="toolBox"
                color="currentColor"
                fontSize={18}
              />
            </Button>
          </Tooltip>
          {aiToolsExpanded && (
            <div className={styles.aiToolsCollapsedList}>
              {SIDEBAR_AI_TOOLS.map((tool: any) => (
                <Button
                  key={tool.id}
                  type="transparent"
                  className={classNames(styles.aiToolCollapsedItem, {
                    [styles.active]: activeNav === tool.id,
                  })}
                  onClick={() => {
                    aiToolClicked(tool);
                  }}
                >
                  <IconFont
                    name={tool.icon}
                    color="currentColor"
                    fontSize={18}
                  />
                  <span className={styles.aiToolCollapsedItemName}>
                    {languageStore.smartcatTranslationText((v) => v[tool.title as keyof typeof v])}
                  </span>
                </Button>
              ))}
            </div>
          )}
        </div>
        {(activeNav === HomeBar.Home || activeNav === HomeBar.Assets) && (
          <NavbarAssetStorageCard
            className={styles.collapsedAssetStorageCard}
            variant="compact"
          />
        )}
      </div>

      {/* 展开 */}
      <div
        className={styles.navbarBox}
        style={{
          opacity: !expand ? '1' : '0',
          zIndex: !expand ? 2 : 1,
          pointerEvents: !expand ? 'auto' : 'none',
        }}
      >
        {/* 顶部logo */}
        <div className={styles.sidebarHeader}>
          <div
            className={styles.logoBox}
            onClick={goHomepage}
            style={{
              filter: theme === 'light' ? 'brightness(0)' : 'none',
            }}
          >
            <span className={styles.logoMark}>
              <Image
                src={theme === 'light' ? COLLAPSE_LOGO_BLACK : COLLAPSE_LOGO}
                alt=""
                width={28}
                height={18}
              />
            </span>
            <Image
              className={styles.logoTitle}
              src={ROBONEO_TITLE}
              alt="RoboNeo"
              width={116}
              height={15}
            />
          </div>
          <Tooltip
            title={languageStore.smartcatTranslationText((v) => v.roboneo_web_collapse_sidebar)}
            placement="right"
          >
            <div className={styles.collapseBox}>
              <IconFont
                className={styles.collapse}
                name="sidebarLeftSquareBold"
                color="currentColor"
                fontSize={16}
                onClick={() => handleNavbarExpandChange(!expand)}
              />
            </div>
          </Tooltip>
        </div>

        <div className={styles.navBox}>
          <Button
            type="transparent"
            className={classNames(styles.nav_btn, styles.create_btn)}
            leftIcon={<IconFont name="plusCircleFill" fontSize={18} className={styles.icon} />}
            onClick={handleCreateProject}
          >
            {/* 新建项目 */}
            {languageStore.smartcatTranslationText((v) => v.roboneo_web_project)}
          </Button>
          {BarList.map((item) => (
            <Button
              key={item.id}
              type="transparent"
              className={classNames(styles.nav_btn, { [styles.active]: activeNav === item.id })}
              leftIcon={<IconFont name={activeNav === item.id ? item.activeIcon : item.icon} fontSize={18} className={styles.icon} />}
              onClick={() => goTabItem(item.id)}
            >
              {item.name}
            </Button>
          ))}
          {/* AI创作工具 可展开列表 */}
          <div className={styles.aiToolsSection}>
            <div
              className={styles.aiToolsHeader}
              onClick={() => setAiToolsExpanded((v) => !v)}
            >
              <div className={styles.aiToolsHeaderLeft}>
                <IconFont name="toolBox" fontSize={18} className={styles.icon} color="currentColor" />
                <span className={styles.aiToolsTitle}>{languageStore.smartcatTranslationText((v) => v.ai_create_tools)}</span>
              </div>
            </div>
            <div className={classNames(styles.aiToolsList, { [styles.aiToolsListExpanded]: aiToolsExpanded })}>
              {SIDEBAR_AI_TOOLS.map((tool: any) => (
                <div
                  key={tool.id}
                  className={classNames(styles.aiToolItem, { [styles.active]: activeNav === tool.id })}
                  onClick={() => { aiToolClicked(tool); }}
                >
                  <IconFont name={tool.icon} fontSize={18} className={styles.icon} color="var(--content-primary)" />
                  <span className={styles.aiToolItemName}>{languageStore.smartcatTranslationText((v) => v[tool.title as keyof typeof v])}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {(activeNav === HomeBar.Home || activeNav === HomeBar.Assets) && (
          <NavbarAssetStorageCard
            className={styles.homeAssetStorageCard}
            visible={!expand}
          />
        )}
      </div>
    </div>
  );
}

export default Navbar;
